/**
 * Detección de episodios en el cliente. Espejo exacto de server/episodeAnalysis.js
 * para que la simulación del calibrador visual coincida con el resultado publicado.
 */

export function parseSeriesTimestamp(value) {
  if (value === null || value === undefined || value === '') return null;
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return Date.UTC(
      value.getUTCFullYear(),
      value.getUTCMonth(),
      value.getUTCDate(),
      value.getUTCHours(),
      value.getUTCMinutes(),
      value.getUTCSeconds()
    );
  }
  const s = String(value).trim();
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})(?:[T\s](\d{2}):(\d{2})(?::(\d{2}))?)?/);
  if (!m) return null;
  return Date.UTC(
    Number(m[1]),
    Number(m[2]) - 1,
    Number(m[3]),
    Number(m[4] || 0),
    Number(m[5] || 0),
    Number(m[6] || 0)
  );
}

/** Moda de deltas consecutivos (ms); robusta ante huecos de datos. */
export function inferBucketMs(sortedMs) {
  const counts = new Map();
  for (let i = 1; i < sortedMs.length; i += 1) {
    const d = sortedMs[i] - sortedMs[i - 1];
    if (d > 0) counts.set(d, (counts.get(d) || 0) + 1);
  }
  if (!counts.size) return 60000;
  let best = null;
  for (const [delta, count] of counts) {
    if (!best || count > best.count || (count === best.count && delta < best.delta)) {
      best = { delta, count };
    }
  }
  return best.delta;
}

export function smoothValues(values, type, window) {
  if (!type || type === 'none') return values;
  const w = Math.max(3, Number(window) || 3);
  const half = Math.floor(w / 2);
  return values.map((_, i) => {
    const slice = values.slice(Math.max(0, i - half), Math.min(values.length, i + half + 1));
    if (type === 'mean') {
      return slice.reduce((a, b) => a + b, 0) / slice.length;
    }
    const sorted = [...slice].sort((a, b) => a - b);
    return sorted[Math.floor(sorted.length / 2)];
  });
}

function buildStateClassifier(cfg) {
  const t = Number(cfg.threshold);
  const tHigh =
    cfg.thresholdHigh != null && cfg.thresholdHigh !== '' ? Number(cfg.thresholdHigh) : null;
  const exit =
    cfg.exitThreshold != null && cfg.exitThreshold !== '' ? Number(cfg.exitThreshold) : null;

  if (cfg.condition === 'above') {
    const exitT = exit != null ? Math.min(exit, t) : t;
    return { enter: (v) => v > t, stay: (v) => v > exitT };
  }
  if (cfg.condition === 'insideBand') {
    if (tHigh == null) return null;
    return { enter: (v) => v >= t && v <= tHigh, stay: (v) => v >= t && v <= tHigh };
  }
  if (cfg.condition === 'outsideBand') {
    if (tHigh == null) return null;
    return { enter: (v) => v < t || v > tHigh, stay: (v) => v < t || v > tHigh };
  }
  const exitT = exit != null ? Math.max(exit, t) : t;
  return { enter: (v) => v < t, stay: (v) => v < exitT };
}

export function classifyStates(values, cfg) {
  const classifier = buildStateClassifier(cfg);
  if (!classifier) return new Array(values.length).fill(false);
  const { enter, stay } = classifier;
  const states = new Array(values.length).fill(false);
  let active = false;
  for (let i = 0; i < values.length; i += 1) {
    active = active ? stay(values[i]) : enter(values[i]);
    states[i] = active;
  }
  return states;
}

function fillMissingWithZero(points, bucketMs) {
  if (points.length < 2) return points;
  const out = [];
  for (let i = 0; i < points.length; i += 1) {
    out.push(points[i]);
    if (i + 1 < points.length) {
      let t = points[i].ms + bucketMs;
      let guard = 0;
      while (t < points[i + 1].ms && guard < 100000) {
        out.push({ ms: t, value: 0, filled: true });
        t += bucketMs;
        guard += 1;
      }
    }
  }
  return out;
}

function statesToEpisodes(points, states, bucketMs) {
  const episodes = [];
  let start = null;
  let lastActiveMs = null;
  for (let i = 0; i < points.length; i += 1) {
    const gapBroken =
      lastActiveMs != null && start != null && points[i].ms - lastActiveMs > bucketMs;
    if (start != null && (!states[i] || gapBroken)) {
      episodes.push({ startMs: start, endMs: lastActiveMs + bucketMs });
      start = null;
      lastActiveMs = null;
    }
    if (states[i]) {
      if (start == null) start = points[i].ms;
      lastActiveMs = points[i].ms;
    }
  }
  if (start != null && lastActiveMs != null) {
    episodes.push({ startMs: start, endMs: lastActiveMs + bucketMs });
  }
  return episodes;
}

export function mergeEpisodeGaps(episodes, gapToleranceMin) {
  const tolMs = Math.max(0, Number(gapToleranceMin) || 0) * 60000;
  if (!tolMs || episodes.length < 2) return episodes;
  const out = [{ ...episodes[0] }];
  for (let i = 1; i < episodes.length; i += 1) {
    const prev = out[out.length - 1];
    if (episodes[i].startMs - prev.endMs <= tolMs) {
      prev.endMs = Math.max(prev.endMs, episodes[i].endMs);
    } else {
      out.push({ ...episodes[i] });
    }
  }
  return out;
}

/**
 * Igual que el servidor, pero además devuelve los episodios descartados por
 * duración mínima y la serie suavizada, para visualizarlos en el calibrador.
 */
export function detectEpisodesDetailed(points, cfg) {
  if (!points.length) {
    return { episodes: [], discarded: [], smoothed: [], filledPoints: [], bucketMs: 60000 };
  }
  const sorted = [...points].sort((a, b) => a.ms - b.ms);
  const bucketMs = inferBucketMs(sorted.map((p) => p.ms));
  const filled = cfg.missingAsZero ? fillMissingWithZero(sorted, bucketMs) : sorted;
  const smoothed = smoothValues(
    filled.map((p) => p.value),
    cfg.smoothing,
    cfg.smoothingWindow
  );
  const states = classifyStates(smoothed, cfg);
  let episodes = statesToEpisodes(filled, states, bucketMs);
  episodes = mergeEpisodeGaps(episodes, cfg.gapToleranceMin);

  const minMs = Math.max(0, Number(cfg.minDurationMin) || 0) * 60000;
  const kept = [];
  const discarded = [];
  for (const e of episodes) {
    if (!minMs || e.endMs - e.startMs >= minMs) kept.push(e);
    else discarded.push(e);
  }
  return { episodes: kept, discarded, smoothed, filledPoints: filled, bucketMs };
}
